import { HeartHandshake, MapPin, ShieldCheck, Users } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { CONTACT, IMAGES } from "@/data/content";

const VALUES = [
  { icon: HeartHandshake, title: "Unhurried by design", text: "Visits are never less than an hour, and never rushed. A proper chat needs room to breathe." },
  { icon: ShieldCheck, title: "Safety before everything", text: "Every befriender is PVG checked and personally interviewed before their first visit. No exceptions, ever." },
  { icon: Users, title: "Matched, not allocated", text: "Shared interests, personality and humour come first. The right person matters more than the nearest one." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About us — Brightside Befriending Services, Glasgow"
        siteName="Brightside Befriending Services"
        description="Brightside Befriending Services Ltd is a Glasgow company providing private, PVG-checked companionship for older adults across Glasgow and surrounding towns, Ayr and South Ayrshire."
      />
      <PageHero
        eyebrow="About Brightside"
        lines={["Built in Glasgow,", "for the people", "who built it."]}
        text="Brightside Befriending Services Ltd is a small, founder-led company with one aim: to make sure older people across Glasgow always have someone to talk to, laugh with and look forward to seeing."
      />

      <section className="px-4 pb-24 sm:px-6 lg:px-8" data-testid="about-story">
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="photo-frame overflow-hidden rounded-[2.5rem] shadow-[0_30px_60px_rgba(28,56,41,0.18)]">
              <img src={IMAGES.tea} alt="Two people enjoying tea and warm conversation together" className="h-[26rem] w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <SectionHeading eyebrow="Our story" title="Company, not care" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-pebble">
                <p>
                  Brightside began with a simple observation: some older people in Glasgow don't qualify for council support, sit for months on charity waiting lists, or simply want something more personal — a familiar face who comes because they enjoy the company, not because a rota says so.
                </p>
                <p>
                  We're not a care agency. We don't do personal or nursing care. What we do is the bit that often matters most to families — conversation, outings, errands shared together, and a regular friendly voice on the phone. For families who live far away, we become the eyes, ears and warm presence in between their own visits.
                </p>
                <p>
                  {CONTACT.company} is a private limited company registered with Companies House, working from our city-centre office on West George Street and out in the communities we serve.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white/50 px-4 py-24 sm:px-6 lg:px-8" data-testid="about-values">
        <div className="mx-auto max-w-7xl">
          <SectionHeading eyebrow="What we stand for" title="Three promises we make to every family" />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <article className="h-full rounded-3xl border border-sand bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_rgba(28,56,41,0.12)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-tint text-sage">
                    <v.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-serif text-xl text-forest">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-pebble">{v.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8" data-testid="about-coverage">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="Where we work" title="Glasgow, and just beyond" />
              <Reveal delay={0.15}>
                <p className="mt-6 text-base leading-relaxed text-pebble">
                  Our befrienders cover Glasgow city and every surrounding town — plus Ayr, Prestwick, Troon and the South Ayrshire coast. If you're just outside these areas, ask anyway — we're growing, and we'll always be honest about what we can do.
                </p>
              </Reveal>
              <div className="mt-8 flex flex-wrap gap-3">
                {CONTACT.areas.map((area, i) => (
                  <Reveal key={area} delay={0.1 + i * 0.06}>
                    <span data-testid={`coverage-chip-${area.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="inline-flex items-center gap-2 rounded-full border border-sage/30 bg-sage-tint px-4 py-2 text-sm font-medium text-forest">
                      <MapPin className="h-3.5 w-3.5 text-terracotta" /> {area}
                    </span>
                  </Reveal>
                ))}
              </div>
            </div>
            <Reveal delay={0.15}>
              <div className="photo-frame overflow-hidden rounded-[2.5rem] shadow-[0_30px_60px_rgba(28,56,41,0.18)]">
                <img src={IMAGES.loch} alt="A peaceful bench overlooking Scottish greenery and water" className="h-[24rem] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection title="Come and meet us properly" text="The best way to understand Brightside is a free, unhurried conversation — with you, your parent, or both." />
    </>
  );
}
